import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { fetchCSRFToken, getCSRFToken } from '../../lib/csrf';
import i18n from '../../i18n';
import css from './survey.module.css';
import { trackSurveyCompleted } from '../../lib/analytics';


const getAvailableLanguages = (t) => [
    { code: "en", name: t("English"), flag: "🇬🇧" },
    { code: "uk", name: t("Ukrainian"), flag: "🇺🇦" },
    { code: "pl", name: t("Polish"), flag: "🇵🇱" },
];

const translations = {
    en: {
        levels: [
            "Ideal for beginners",
            "Starting from zero",
            "Understand simple phrases",
            "Talk about familiar topics",
            "Speak fluently",
            "Discuss complex ideas"
        ],
        purposes: [
            "Travel preparation",
            "Job interview",
            "Educational goals",
            "Communication with people",
            "For fun"
        ]
    },
    uk: {
        levels: [
            "Ідеально для початківців",
            "Починаю з нуля",
            "Розумію прості фрази",
            "Розмовляю на знайомі теми",
            "Вільно спілкуюся",
            "Обговорюю складні ідеї"
        ],
        purposes: [
            "Підготовка до подорожі",
            "Для співбесіди на роботу",
            "Освітні цілі",
            "Спілкування з людьми",
            "Задля розваги"
        ]
    },
    pl: {
        levels: [
            "Idealny dla początkujących",
            "Zaczynam od zera",
            "Rozumiem proste zwroty",
            "Rozmawiam na znane tematy",
            "Swobodnie się komunikuję",
            "Omawiam złożone idee"
        ],
        purposes: [
            "Przygotowanie do podróży",
            "Rozmowa kwalifikacyjna",
            "Cele edukacyjne",
            "Komunikacja z ludźmi",
            "Dla zabawy"
        ]
    }
};

const buildLevelQuestions = (lang) => {
    const t = translations[lang] || translations.en;
    return t.levels.map((text, index) => ({
        id: index === 0 ? 7 : index,
        option: text,
        levels: index === 0 ? ['pre-A1'] : (index === 5 ? ['C1', 'C2'] : [['A1', 'A2', 'B1', 'B2'][index - 1]])
    }));
};

const buildPurposes = (lang) => {
    const t = translations[lang] || translations.en;
    return t.purposes.map((text, index) => ({
        id: index + 1,
        purpose: text,
        categories: [index + 1]
    }));
};

const Survey = ({ authUser }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const particlesContainerRef = useRef(null);

    const [currentPage, setCurrentPage] = useState(() => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('skipLanguage') === 'true' ? 'survey' : 'language';
    });
    const [currentLanguage, setCurrentLanguage] = useState(
        localStorage.getItem("selectedLanguage") || "en"
    );
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);

    const [surveyData, setSurveyData] = useState({
        language: null,
        englishLevel: null,
        purpose: null
    });

    const [levelQuestions, setLevelQuestions] = useState(() => buildLevelQuestions(localStorage.getItem("selectedLanguage") || "en"));
    const [purposes, setPurposes] = useState(() => buildPurposes(localStorage.getItem("selectedLanguage") || "en"));

    const [completedSteps, setCompletedSteps] = useState({
        level: false,
        purpose: false
    });

    // Add/remove scrollbar styles
    useEffect(() => {
        document.body.classList.add('surveyPageActive');
        return () => {
            document.body.classList.remove('surveyPageActive');
        };
    }, []);

    // Initialize particles effect
    useEffect(() => {
        if (particlesContainerRef.current) {
            const container = particlesContainerRef.current;
            container.innerHTML = '';
            for (let i = 0; i < 50; i++) {
                const particle = document.createElement('div');
                particle.className = css.particle;
                particle.style.left = Math.random() * 100 + '%';
                particle.style.animationDelay = Math.random() * 15 + 's';
                particle.style.animationDuration = (15 + Math.random() * 10) + 's';
                container.appendChild(particle);
            }
        }
    }, []);

    // Check authentication
    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const res = await axios.get("/api/v1/user-profile/current", {
                    withCredentials: true,
                });
                const lang = res.data.language || "en";
                setCurrentLanguage(lang);
                i18n.changeLanguage(lang);
                setIsAuthenticated(true);
            } catch (error) {
                console.warn("❌ Неавторизований або 403 — працюємо як гість");
                setIsAuthenticated(false);
            }
        };
        fetchUserProfile();
    }, []);

    // Update questions instantly whenever language changes — no fetching, no flash
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = i18n.language;
            setCurrentLanguage(lang);
            setLevelQuestions(buildLevelQuestions(lang));
            setPurposes(buildPurposes(lang));
        };

        i18n.on('languageChanged', handleLanguageChange);
        // Set immediately for current language on mount
        handleLanguageChange();

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);

    // Handle language selection
    const handleLanguageSelect = async (langCode) => {
        setLoading(true);
        try {
            document.cookie = `django_language=${langCode}; path=/`;
            i18n.changeLanguage(langCode);
            localStorage.setItem("selectedLanguage", langCode);
            setCurrentLanguage(langCode);

            if (isAuthenticated) {
                let csrfToken = getCSRFToken();
                if (!csrfToken) {
                    await fetchCSRFToken();
                    csrfToken = getCSRFToken();
                }
                await axios.patch(
                    "/api/v1/user-profile/current",
                    { language: langCode },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "X-CSRFToken": csrfToken,
                        },
                        withCredentials: true,
                    }
                );
            }

            setSurveyData(prev => ({ ...prev, language: langCode }));
            setCurrentPage('survey');
        } catch (error) {
            console.error("Помилка при виборі мови:", error.response?.data || error);
        } finally {
            setLoading(false);
        }
    };

    // Handle level selection
    const handleLevelSelect = (questionData) => {
        const selectedLevel = questionData.levels[questionData.levels.length - 1];

        setSurveyData(prev => ({
            ...prev,
            englishLevel: selectedLevel,
            levelQuestionnaireId: questionData.id
        }));

        setCompletedSteps(prev => {
            const newSteps = { ...prev, level: true };
            setTimeout(() => {
                if (!newSteps.purpose) {
                    const purposeCard = document.querySelector('[data-question="2"]');
                    if (purposeCard) purposeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    const submitSection = document.getElementById('submitSection');
                    if (submitSection) submitSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return newSteps;
        });

        localStorage.setItem("selectedLevel", questionData.id.toString());
        localStorage.setItem("selectedLevelCode", selectedLevel);
    };

    // Handle purpose selection
    const handlePurposeSelect = (purposeObj) => {
        const purposeData = {
            id: purposeObj.id,
            purpose: purposeObj.purpose,
            categories: purposeObj.categories || [],
        };

        setSurveyData(prev => ({ ...prev, purpose: purposeData }));

        setCompletedSteps(prev => {
            const newSteps = { ...prev, purpose: true };
            setTimeout(() => {
                if (!newSteps.level) {
                    const levelCard = document.querySelector('[data-question="1"]');
                    if (levelCard) levelCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    const submitSection = document.getElementById('submitSection');
                    if (submitSection) submitSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return newSteps;
        });

        localStorage.setItem("selectedPurpose", JSON.stringify(purposeData));
    };

    // Handle step dot click
    const handleStepClick = (stepNumber) => {
        const targetCard = document.querySelector(`[data-question="${stepNumber}"]`);
        if (targetCard) targetCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Submit survey
    const handleSubmitSurvey = async () => {
        setLoading(true);
        try {
            if (surveyData.levelQuestionnaireId) {
                localStorage.setItem("selectedLevel", surveyData.levelQuestionnaireId.toString());
            }
            if (surveyData.purpose) {
                localStorage.setItem("selectedPurpose", JSON.stringify(surveyData.purpose));
            }

            const levelCefr = localStorage.getItem("selectedLevelCode") || surveyData.englishLevel || '';

            let goal = '';
            if (surveyData.purpose?.purpose) {
                const purposeText = surveyData.purpose.purpose.toLowerCase();
                if (purposeText.includes('travel') || purposeText.includes('подорож') || purposeText.includes('podróże')) {
                    goal = 'travel';
                } else if (purposeText.includes('work') || purposeText.includes('job') || purposeText.includes('робот') || purposeText.includes('praca')) {
                    goal = 'work';
                } else if (purposeText.includes('education') || purposeText.includes('study') || purposeText.includes('освіт') || purposeText.includes('edukacja')) {
                    goal = 'study';
                } else if (purposeText.includes('communication') || purposeText.includes('спілкування') || purposeText.includes('komunikacja')) {
                    goal = 'social';
                } else if (purposeText.includes('entertainment') || purposeText.includes('розваг') || purposeText.includes('zabaw')) {
                    goal = 'kids';
                }
            }

            trackSurveyCompleted(levelCefr, goal, '');
            confetti();
            setTimeout(() => navigate("/roadMap"), 1500);

        } catch (error) {
            console.error("Error submitting survey:", error);
        } finally {
            setLoading(false);
        }
    };

    // Confetti animation
    const confetti = () => {
        const colors = ['#9333ea', '#6366f1', '#a855f7', '#818cf8'];
        for (let i = 0; i < 100; i++) {
            const el = document.createElement('div');
            el.style.cssText = `position:fixed;width:10px;height:10px;background:${colors[Math.floor(Math.random()*colors.length)]};left:${Math.random()*100}%;top:-10px;opacity:${Math.random()+0.5};transform:rotate(${Math.random()*360}deg);transition:all 3s ease-out;z-index:9999`;
            document.body.appendChild(el);
            setTimeout(() => {
                el.style.top = '100%';
                el.style.transform = `rotate(${Math.random()*360}deg) translateX(${(Math.random()-0.5)*200}px)`;
                el.style.opacity = '0';
            }, 10);
            setTimeout(() => el.remove(), 3000);
        }
    };

    return (
        <div className={css.surveyContainer}>
            <div className={css.background}></div>

            {/* Language Selection Page */}
            {currentPage === 'language' && (
                <div className={`${css.page} ${css.languagePage} ${css.active}`}>
                    <div className={css.languageContainer}>
                        <h1 className={css.languageTitle}>
                            {t("In which language would you like to view the website?")}
                        </h1>
                        <div className={css.languageGrid}>
                            {getAvailableLanguages(t).map((lang) => (
                                <div
                                    key={lang.code}
                                    className={css.languageCard}
                                    onClick={() => handleLanguageSelect(lang.code)}
                                >
                                    <span className={css.languageFlag}>{lang.flag}</span>
                                    <div className={css.languageName}>{lang.name}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Survey Page */}
            {currentPage === 'survey' && (
                <div className={`${css.page} ${css.surveyPage} ${css.active}`}>
                    <div className={css.progressSteps}>
                        <div
                            className={`${css.stepDot} ${completedSteps.level ? css.completed : css.active}`}
                            data-step="1"
                            onClick={() => handleStepClick(1)}
                        ></div>
                        <div
                            className={`${css.stepDot} ${completedSteps.purpose ? css.completed : (completedSteps.level ? css.active : '')}`}
                            data-step="2"
                            onClick={() => handleStepClick(2)}
                        ></div>
                    </div>

                    <div className={css.hero}>
                        <h1 className={css.heroTitle}>
                            {t("Take the test — get a program personalized to your goal and level")}
                        </h1>
                        <p className={css.heroSubtitle}>
                            {t("It will take less than 1 minute")}
                        </p>
                    </div>

                    <div className={css.container}>
                        {/* Question 1: English Level */}
                        <div className={`${css.questionCard} ${css.visible}`} data-question="1">
                            <h2 className={css.questionTitle}>
                                {t("What is your current English level?")}
                            </h2>
                            <div className={css.options}>
                                {levelQuestions.map((question, index) => {
                                    const currentLevel = question.levels[question.levels.length - 1];
                                    const isSelected = surveyData.englishLevel === currentLevel;
                                    const levelNames = ["pre-A1", 'A1', 'A2', 'B1', 'B2', 'C1-C2'];

                                    return (
                                        <div
                                            key={question.id}
                                            className={`${css.option} ${isSelected ? css.selected : ''} ${index >= 3 ? css.blocked : ""}`}
                                            onClick={() => { if (index < 3) handleLevelSelect(question); }}
                                        >
                                            {index >= 3 && (
                                                <svg className={css.lockIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                    <path d="M7 11V7a5 5 0 0110 0v4"></path>
                                                </svg>
                                            )}
                                            <span className={css.levelCode}>{levelNames[index]}</span>
                                            <p className={css.levelDescription}>{question.option}</p>
                                            {index >= 4 && (
                                                <span className={css.comingSoon}>{t("coming soon")}</span>
                                            )}
                                            <div className={css.checkIcon}>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M3 8L6 11L13 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                                </svg>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Question 2: Purpose */}
                        <div className={`${css.questionCard} ${css.visible}`} data-question="2">
                            <h2 className={css.questionTitle}>
                                {t("What's your main goal with English?")}
                            </h2>
                            <div className={css.options}>
                                {purposes.map((purpose) => {
                                    const isSelected = surveyData.purpose?.id === purpose.id;
                                    return (
                                        <div
                                            key={purpose.id}
                                            className={`${css.optionP} ${isSelected ? css.selected : ''}`}
                                            onClick={() => handlePurposeSelect(purpose)}
                                        >
                                            <div className={css.purposeIcon}>
                                                {getPurposeIcon(purpose.purpose)}
                                            </div>
                                            <div className={css.optionText}>{purpose.purpose}</div>
                                            <div className={css.checkIcon}>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                    <path d="M3 8L6 11L13 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                                                </svg>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Submit Section */}
                        {completedSteps.level && completedSteps.purpose && (
                            <div className={`${css.submitSection} ${css.show}`} id="submitSection">
                                <h3 className={css.completionMessage}>
                                    🎉 {t("Great! We've prepared your personalized learning path")}
                                </h3>
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <button
                                        className={css.getPlanBtn}
                                        onClick={handleSubmitSurvey}
                                        disabled={loading}
                                    >
                                        <div className={css.getPlanBtnContent}>
                                            {!loading && <span className={css.getPlanBtnIcon}>✨</span>}
                                            <span>{loading ? t("Processing...") : t("Finish test")}</span>
                                            {loading && <div className={css.loading}></div>}
                                        </div>
                                    </button>
                                    <div className={css.sparkle}></div>
                                    <div className={css.sparkle}></div>
                                    <div className={css.sparkle}></div>
                                    <div className={css.sparkle}></div>
                                    <div className={css.sparkle}></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const getPurposeIcon = (purpose) => {
    const p = purpose.toLowerCase();
    if (p.includes('travel') || p.includes('подорож') || p.includes('podróży')) return '✈️';
    if (p.includes('job') || p.includes('робот') || p.includes('praca') || p.includes('kwalifikacyjna')) return '💼';
    if (p.includes('education') || p.includes('освіт') || p.includes('edukac')) return '🎓';
    if (p.includes('communication') || p.includes('спілкування') || p.includes('komunikac')) return '💬';
    if (p.includes('entertainment') || p.includes('розваг') || p.includes('rozrywk') || p.includes('fun') || p.includes('zabawy')) return '🎬';
    return '🎯';
};

export default Survey;